// Promo codes (discount at checkout) and redeem codes (a plan grant). The contract is
// docs/designs/promo-codes.md in uptrack-server, sections "API" and "Web".
//
// Rules for this file and its callers: a code lives in component state only. Never put it in a URL,
// localStorage, sessionStorage, a query key, a log line or an analytics event.
import { ApiV2Error } from '@/lib/api-v2-client'
import { tiers, type BillingInterval, type PaidPlan } from '@/lib/plans'

/** The API names its intervals `monthly` and `yearly`; the web uses `monthly` and `annual`. */
export type PromoInterval = 'monthly' | 'yearly'

export function toPromoInterval(interval: BillingInterval): PromoInterval {
  return interval === 'annual' ? 'yearly' : 'monthly'
}

export const PROMO_COPY = {
  link: 'Have a promo code?',
  label: 'Promo code',
  invalid: 'This code is not valid.',
  grantExists: 'Your organization already has a free plan from a code or a program.',
  failed: 'We could not check this code. Try again.',
  checking: 'Checking the code…',
  redeemTitle: 'Redeem a code',
  redeemIntro: 'Have a code for a plan? Enter it here.',
  redeemLabel: 'Code',
  redeemButton: 'Redeem',
  redeeming: 'Redeeming…',
  priceNote: 'Paddle shows the final price at checkout.',
} as const

/** The pieces of a promo answer the sentence needs. `duration_intervals` null means every period. */
export type PromoTerms = { percent_off: number; duration_intervals: number | null }

function percentText(percent: number): string {
  return String(Number(percent.toFixed(2)))
}

/** "N% off your first year". Built from the numbers, never from a server string. */
export function promoSentence(terms: PromoTerms, interval: PromoInterval): string {
  const unit = interval === 'yearly' ? 'year' : 'month'
  const percent = `${percentText(terms.percent_off)}% off`
  const n = terms.duration_intervals
  if (n === null) return `${percent} every period`
  if (n === 1) return `${percent} your first ${unit}`
  return `${percent} your first ${n} ${unit}s`
}

function money(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`
}

/** List price and discounted price of one period of a plan, from `src/lib/plans.ts`. Tax is not included. */
export function promoDiscountedPrice(
  plan: PaidPlan,
  interval: BillingInterval,
  percentOff: number,
): { original: string; discounted: string; per: 'mo' | 'yr' } {
  const tier = tiers.find((t) => t.plan === plan)
  const list = tier ? (interval === 'annual' ? tier.annualTotalPrice : tier.monthlyPrice) : 0
  const originalCents = Math.round(list * 100)
  const percent = Math.min(100, Math.max(0, percentOff))
  const discountedCents = Math.round((originalCents * (100 - percent)) / 100)
  return { original: money(originalCents), discounted: money(discountedCents), per: interval === 'annual' ? 'yr' : 'mo' }
}

/** "45 seconds", "2 minutes". Null when the wait is unknown. */
export function formatWait(seconds: number | undefined): string | null {
  if (seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) return null
  if (seconds < 60) {
    const s = Math.ceil(seconds)
    return `${s} ${s === 1 ? 'second' : 'seconds'}`
  }
  const m = Math.ceil(seconds / 60)
  return `${m} ${m === 1 ? 'minute' : 'minutes'}`
}

/** The message for a 429. */
export function rateLimitedText(retryAfterSecs: number | undefined): string {
  const wait = formatWait(retryAfterSecs)
  return wait ? `Too many tries. Try again in ${wait}.` : 'Too many tries. Try again later.'
}

export type PromoErrorKind =
  | { kind: 'invalid' }
  | { kind: 'rate_limited'; retryAfterSecs: number | undefined }
  | { kind: 'grant_exists' }
  | { kind: 'other' }

/** The three typed errors of the promo endpoints: 422 `promo_invalid`, 429 with Retry-After, 409 `grant_exists`. */
export function classifyPromoError(err: unknown): PromoErrorKind {
  if (!(err instanceof ApiV2Error)) return { kind: 'other' }
  if (err.status === 422 && err.code === 'promo_invalid') return { kind: 'invalid' }
  if (err.status === 409 && err.code === 'grant_exists') return { kind: 'grant_exists' }
  if (err.status === 429) {
    const secs = (err.details as { retry_after_secs?: unknown } | undefined)?.retry_after_secs
    return { kind: 'rate_limited', retryAfterSecs: typeof secs === 'number' ? secs : undefined }
  }
  return { kind: 'other' }
}

/** One text for every failure of a code: the same for every bad case, so a probe learns nothing. */
export function promoErrorText(err: unknown): string {
  const e = classifyPromoError(err)
  if (e.kind === 'invalid') return PROMO_COPY.invalid
  if (e.kind === 'grant_exists') return PROMO_COPY.grantExists
  if (e.kind === 'rate_limited') return rateLimitedText(e.retryAfterSecs)
  return PROMO_COPY.failed
}
